<section
    class="symptoms-section disease-gradient-section position-relative py-5"
    id="symptoms"
    #symptomsSection
    aria-labelledby="symptoms-heading"
    [formGroup]="searchForm"
    (window:resize)="onWindowResize()"
    (window:scroll)="onWindowScroll()"
    *ngIf="!isNull"
>
    <div class="container-fluid">
        <div class="container-xxxl symptoms-section__content pt-4 mt-2">
            <div class="symptoms-section__header pb-3">
                <div class="container-fluid pb-3 px-0">
                    <div class="row mx-0">
                        <h2 id="symptoms-heading" class="section-heading text-white border-white">Symptoms</h2>
                    </div>
                </div>
                <div class="symptoms-section__intro text-white fs-6 fs-md-18" [innerHtml]="intro.bodyText"></div>
            </div>

            <div class="symptoms-section__panel-frame" [class.symptoms-section__panel-frame--single]="isSingleColumnDesktopPanel">
                <div
                    #viewToggleLine
                    class="symptoms-section__toggle-line text-end d-print-none"
                    [class.symptoms-section__toggle-line--stuck]="isViewToggleStuck"
                    *ngIf="showViewToggle"
                >
                    <button
                        type="button"
                        class="symptoms-section__view-toggle"
                        aria-controls="symptoms-list"
                        [attr.aria-expanded]="activeSymptomsView === 'all'"
                        (click)="toggleActiveSymptomsView()"
                    >
                        {{ activeSymptomsView === 'default' ? 'Show all symptoms' : 'Show fewer symptoms' }}
                        <i class="icon-gard-arrow-right ms-1" aria-hidden="true"></i>
                    </button>
                </div>

                <div #symptomsPanel class="symptoms-section__panel bg-white rounded-3 shadow">
                    <div #searchWrap class="symptoms-section__search-wrap bg-white p-3 d-print-none" role="search" [style.top.px]="stickySearchTop" *ngIf="showSearch">
                        <label class="visually-hidden" for="symptoms-search">Search symptoms</label>
                        <i class="icon-gard-search" aria-hidden="true"></i>
                        <input id="symptoms-search" type="search" formControlName="term" autocomplete="off" placeholder="Search for a symptom" aria-controls="symptoms-list" />
                        <p class="visually-hidden" aria-live="polite" aria-atomic="true">{{ searchStatusMessage }}</p>
                    </div>

                    <div class="symptoms-section__list-host p-3 p-md-4">
                        <ng-container *ngIf="hasSearchMatches; else emptySearchResults">
                            <div
                                id="symptoms-list"
                                #clampedList
                                class="symptoms-section__list"
                                [class.symptoms-section__list--desktop]="isDesktopLayout"
                                [class.symptoms-section__list--single-column]="isSingleColumnDesktopPanel"
                                [class.symptoms-section__list--collapsed]="shouldClampCurrentList && !isCurrentListExpanded"
                                [style.max-height.px]="currentListMaxHeight"
                                [style.--symptoms-group-sticky-top]="groupStickyTop + 'px'"
                            >
                                <div class="symptoms-section__column" data-symptoms-column>
                                    <ng-container *ngFor="let group of primaryGroups">
                                        <ng-container *ngTemplateOutlet="frequencyGroup; context: { $implicit: group }"></ng-container>
                                    </ng-container>
                                </div>

                                <div class="symptoms-section__column" data-symptoms-column *ngIf="secondaryGroups.length">
                                    <ng-container *ngFor="let group of secondaryGroups">
                                        <ng-container *ngTemplateOutlet="frequencyGroup; context: { $implicit: group }"></ng-container>
                                    </ng-container>
                                </div>
                            </div>

                            <div class="symptoms-section__more-row d-print-none" *ngIf="shouldClampCurrentList">
                                <button
                                    #moreButton
                                    type="button"
                                    class="symptoms-section__more-button"
                                    aria-controls="symptoms-list"
                                    [attr.aria-expanded]="isCurrentListExpanded"
                                    (click)="toggleCurrentListExpansion()"
                                >
                                    {{ currentListToggleLabel }}
                                    <i class="icon-gard-drop-arrow-down ms-1" [class.symptoms-section__more-icon--up]="isCurrentListExpanded" aria-hidden="true"></i>
                                </button>
                            </div>

                            <section class="symptoms-section__unrelated rounded-3 p-3 pb-2" *ngIf="filteredUnrelatedSymptoms.length" aria-labelledby="symptoms-unrelated-heading">
                                <h3 id="symptoms-unrelated-heading" class="symptoms-section__unrelated-heading fs-6">
                                    <i class="icon-gard-minus" aria-hidden="true"></i>
                                    Unrelated
                                </h3>
                                <p class="symptoms-section__unrelated-note fs-6">{{ unrelatedNoteLead }} <strong>not</strong> part of this disease.</p>

                                <ul
                                    class="symptoms-section__symptom-list symptoms-section__symptom-list--unrelated"
                                    *ngIf="filteredUnrelatedSymptoms.length > 1; else singleUnrelatedSymptom"
                                >
                                    <li *ngFor="let symptom of filteredUnrelatedSymptoms">
                                        <ng-container *ngTemplateOutlet="symptomName; context: { $implicit: symptom }"></ng-container>
                                    </li>
                                </ul>

                                <ng-template #singleUnrelatedSymptom>
                                    <p class="symptoms-section__single-symptom symptoms-section__single-symptom--unrelated mb-0">
                                        <ng-container *ngTemplateOutlet="symptomName; context: { $implicit: filteredUnrelatedSymptoms[0] }"></ng-container>
                                    </p>
                                </ng-template>
                            </section>
                        </ng-container>

                        <ng-template #emptySearchResults>
                            <p id="symptoms-list" class="symptoms-section__empty-results p-4 px-1 mb-0">No symptoms match "{{ searchTerm }}". Try a different word.</p>
                        </ng-template>
                    </div>
                </div>

                <div class="symptoms-section__source white-link text-center py-3 fs-6">
                    <span [innerText]="hpoText"></span>
                    <a [href]="jaxorg" (click)="redirectToHPO($event)" rel="noopener" target="_blank"
                        >Human Phenotype Ontology <span class="symptoms-section__source-link-tail">(HPO) <i class="icon-gard-open-link" aria-hidden="true"></i></span
                    ></a>
                </div>
            </div>
        </div>
    </div>

    <button
        type="button"
        class="blue-effect text-white rounded-pill symptoms-section__return-top px-3 d-print-none"
        [class.symptoms-section__return-top--show]="showSymptomsTopButton"
        [style.left.px]="returnButtonLeft"
        [attr.aria-label]="symptomsTopButtonAriaLabel"
        (click)="onSymptomsTopAction()"
    >
        <i class="icon-gard-arrow-up" aria-hidden="true"></i>
        <span class="fs-6">{{ symptomsTopButtonLabel }}</span>
    </button>

    <div class="background-gradient background-gradient--subtle-circles d-print-none" aria-hidden="true">
        <div class="circle-top-left"></div>
        <div class="circle-lower-right"></div>
    </div>
</section>

<ng-template #frequencyGroup let-group>
    <section class="symptoms-section__group" [ngClass]="'symptoms-section__group--' + group.key" [attr.aria-labelledby]="'symptoms-' + group.key + '-heading'" data-frequency-group>
        <div class="symptoms-section__group-header">
            <h3 class="symptoms-section__group-title fs-18" [id]="'symptoms-' + group.key + '-heading'">{{ group.title }}</h3>
            <p class="symptoms-section__group-note fs-6">{{ group.symptoms.length === 1 ? group.noteOne : group.note }}</p>
        </div>

        <ul class="symptoms-section__symptom-list" *ngIf="group.symptoms.length > 1; else singleGroupSymptom">
            <li *ngFor="let symptom of group.symptoms" data-symptom-row>
                <ng-container *ngTemplateOutlet="symptomName; context: { $implicit: symptom }"></ng-container>
            </li>
        </ul>

        <ng-template #singleGroupSymptom>
            <p class="symptoms-section__single-symptom mb-0" data-symptom-row>
                <ng-container *ngTemplateOutlet="symptomName; context: { $implicit: group.symptoms[0] }"></ng-container>
            </p>
        </ng-template>
    </section>
</ng-template>

<ng-template #symptomName let-symptom>
    <span class="symptoms-section__symptom-name">
        {{ symptom.displayName }}<span class="symptoms-section__medical-term" *ngIf="symptom.showMedicalTerm"> ({{ symptom.medicalTerm }})</span>
    </span>
</ng-template>
